import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { deleteTruckAdmin, getTruckById } from '../../store/trucks';
import { hasManagerAccess } from '../../utils/permissions';
import TruckLocationHistory from '../../components/TruckLocationHistory';


function TruckDetail() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const sessionUser = useSelector((state) => state.session.user);
  const [truck, setTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentDriver = useSelector(state => state.users.find(u => u.id === truck?.assigned_user_id));
  // const associatedAccount = useSelector(state => state.accounts.find(a => a.id === truck?.account_id));

  useEffect(() => {
    const fetchTruck = async () => {
      try {
        const truckData = await dispatch(getTruckById(id));
        setTruck(truckData);
      } catch (error) {
        console.error("Error fetching truck:", error);
        setError("Failed to load truck details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchTruck();
  }, [id, dispatch]);

  const handleDelete = async () => {
    if (!hasManagerAccess(sessionUser)) {
      setError("You are not authorized to delete this truck.");
      return;
    }

    if (window.confirm('Are you sure you want to delete this truck?')) {
      try {
        await dispatch(deleteTruckAdmin(truck.id));
        history.push('/');
      } catch (error) {
        console.error('Failed to delete truck:', error);
        setError("An error occurred while deleting the truck.");
      }
    }
  };

  if (loading) {
    return <div className="p-5 text-7xl">Loading...</div>;
  }

  if (!truck) {
    return <div className="p-5 text-7xl">Truck not found</div>;
  }

  const getStatusText = () => {
    if (truck.red_tagged) return truck.tagged_description || 'Red Tagged';
    if (truck.yellow_tagged) return truck.tagged_description || 'Yellow Tagged';
    if (truck.in_shop) return 'In Shop';
    return 'Available';
  };

  const getStatusClass = () => {
    if (truck.red_tagged) return 'bg-red-500 text-white';
    if (truck.yellow_tagged) return 'bg-yellow-500 text-black';
    if (truck.in_shop) return 'bg-blue-500 text-white';
    return 'bg-green-500 text-white';
  };

  return (
    <div className="p-5 min-h-[calc(100vh-100px)] flex flex-col">
      <div className="max-w-6xl mx-auto w-full bg-white rounded-lg shadow-md p-8">
        <div className="mb-8">
          <h2 className="text-6xl font-bold text-blue-600">Truck Detail</h2>
          <div className={`mt-2 inline-block px-4 py-2 rounded-md text-xl ${getStatusClass()}`}>
            {getStatusText()}
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 text-2xl" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {/* Two-column layout */}
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {/* Left Column */}
          <div className="md:w-1/2 space-y-6 flex flex-col">
            {/* General Information */}
            <div className="bg-gray-50 rounded-lg p-6 flex-1">
              <h3 className="text-4xl font-semibold mb-4">General Information</h3>
              <div className="space-y-4 text-2xl">
                <p className="flex items-center">
                  <span className="font-semibold w-40">Location:</span>
                  <span>{truck.truck_location}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Assigned Driver:</span>
                  <span>{currentDriver ? `${currentDriver.alpha_code} (${currentDriver.email})` : 'Not assigned'}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Last User:</span>
                  <span>{truck.last_user || 'N/A'}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">License Plate:</span>
                  <span>{truck.license_plate}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">License State:</span>
                  <span>{truck.license_state}</span>
                </p>
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="bg-gray-50 rounded-lg p-6 flex-1">
              <h3 className="text-4xl font-semibold mb-4">Vehicle Details</h3>
              <div className="space-y-4 text-2xl">
                <p className="flex items-center">
                  <span className="font-semibold w-40">Truck Number:</span>
                  <span>{truck.truck_number}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Type:</span>
                  <span>{truck.type}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Year:</span>
                  <span>{truck.year}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Make:</span>
                  <span>{truck.make}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Model:</span>
                  <span>{truck.model}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="md:w-1/2 space-y-6 flex flex-col">
            {/* Status Information */}
            <div className="bg-gray-50 rounded-lg p-6 flex-1">
              <h3 className="text-4xl font-semibold mb-4">Status Information</h3>
              <div className="space-y-4 text-2xl">
                <p className="flex items-center">
                  <span className="font-semibold w-40">Red Tagged:</span>
                  <span className="flex items-center">
                    <span className={`h-3 w-3 rounded-full mr-2 ${truck.red_tagged ? 'bg-red-500' : 'bg-gray-300'}`}></span>
                    {truck.red_tagged ? 'Yes' : 'No'}
                  </span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Yellow Tagged:</span>
                  <span className="flex items-center">
                    <span className={`h-3 w-3 rounded-full mr-2 ${truck.yellow_tagged ? 'bg-yellow-500' : 'bg-gray-300'}`}></span>
                    {truck.yellow_tagged ? 'Yes' : 'No'}
                  </span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">In Shop:</span>
                  <span className="flex items-center">
                    <span className={`h-3 w-3 rounded-full mr-2 ${truck.in_shop ? 'bg-blue-500' : 'bg-gray-300'}`}></span>
                    {truck.in_shop ? 'Yes' : 'No'}
                  </span>
                </p>
                {(truck.red_tagged || truck.yellow_tagged) && (
                  <div className="mt-4">
                    <p className="font-semibold mb-2">Tagged Description:</p>
                    <div
                      className={`p-3 rounded-md ${
                        truck.red_tagged
                          ? 'bg-red-500 text-white'
                          : truck.yellow_tagged
                          ? 'bg-yellow-500 text-black'
                          : ''
                      }`}
                    >
                      {truck.tagged_description || 'No description provided'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Maintenance Information */}
            <div className="bg-gray-50 rounded-lg p-6 flex-1">
              <h3 className="text-4xl font-semibold mb-4">Maintenance Information</h3>
              <div className="space-y-4 text-2xl">
                <p className="flex items-center">
                  <span className="font-semibold w-40">Last Inspection:</span>
                  <span>{truck.last_inspection ? new Date(truck.last_inspection).toLocaleDateString() : 'N/A'}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Next Inspection:</span>
                  <span>{truck.next_inspection ? new Date(truck.next_inspection).toLocaleDateString() : 'N/A'}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Last Maintenance:</span>
                  <span>{truck.last_maintenance ? new Date(truck.last_maintenance).toLocaleDateString() : 'N/A'}</span>
                </p>
                <p className="flex items-center">
                  <span className="font-semibold w-40">Next Maintenance:</span>
                  <span>{truck.next_maintenance ? new Date(truck.next_maintenance).toLocaleDateString() : 'N/A'}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="w-full flex justify-between items-center mb-8">
          <div className="">
            <button id = 'truck-back-button'
              onClick={() => history.push('/')}
              className="px-8 py-4 text-3xl text-gray-700 bg-gray-200 rounded-lg transition-all duration-200
                hover:bg-gray-300 hover:shadow-md hover:scale-[1.02]"
            >
              Back
            </button>
          </div>
          <div className="space-x-4">
            {hasManagerAccess(sessionUser) && (
              <button id = 'truck-delete-button'
                onClick={handleDelete}
                className="px-6 py-3 text-2xl text-white bg-red-500 rounded-lg transition-all duration-200
                  hover:bg-red-600 hover:shadow-md hover:scale-[1.02]"
              >
                Delete
              </button>
            )}
            <button id = 'truck-update-button'
              onClick={() => history.push(`/trucks/${id}/edit`)}
              className="inline-block px-6 py-3 text-2xl text-white bg-blue-500 rounded-lg transition-all duration-200
                hover:bg-blue-600 hover:shadow-md hover:scale-[1.02]"
              disabled={!hasManagerAccess(sessionUser)}
            >
              Update
            </button>
          </div>
        </div>

        {/* Location History (Full Width) */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-4xl font-semibold mb-4">Location History</h3>
          <TruckLocationHistory truckId={id} />
        </div>

      </div>
    </div>
  );
}

export default TruckDetail;
